// ** React Imports
import { useState, useEffect, useMemo, ChangeEvent } from 'react'

// ** Icons
import { Eye, EyeSlash } from '@phosphor-icons/react'

// ** UI Components
import { Icon, Input } from '@shopflo/ui'
import { Switch } from '@mui/material'

// ** Type Imports
import { YcloudAnalyticsType } from 'src/lib/types/integrations'
import { ChannelConfigType } from 'src/lib/types/channels'

// ** Child Imports
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import SaveNotification from 'src/components/common/SaveNotification'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig, verifyAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialYcloudAnalyticsState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { useLocale } from 'src/lib/hooks/useLocal'
import { getEnabledChannelsArray } from 'src/lib/utils/parsers'

interface YcloudProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const INPUT_ID = 'ycloud-api-key'
const TOGGLE_LABEL_ID = 'ycloud-toggle-label'

type ApiResponse = { error?: string; data?: any; headers?: Record<string, string> }

const Ycloud = ({ isOnboarding = false, handleOnboardingSave }: YcloudProps): JSX.Element => {
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { t } = useLocale()
  const { ycloud } = integrations || {}

  const [ycloudAnalytics, setYcloudAnalytics] = useState<YcloudAnalyticsType>(initialYcloudAnalyticsState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [showApiKey, setShowApiKey] = useState<boolean>(false)

  const settingsUpdated = useMemo(
    () => JSON.stringify(ycloud) !== JSON.stringify(ycloudAnalytics),
    [ycloud, ycloudAnalytics]
  )

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary?.integrations?.ycloud) return
    setYcloudAnalytics(temporary.integrations.ycloud)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    if (!ycloud) return
    setYcloudAnalytics(ycloud)
    setTemporaryData(undefined)
  }, [ycloud, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise<boolean>((resolve, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            ycloud: ycloudAnalytics
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response = (await getIntegrations()) as ApiResponse
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const isValidForm = () => {
    if (ycloudAnalytics?.enabled && !ycloudAnalytics?.apiKey?.trim()?.length) {
      setOpenAlert('error')
      setAlertMessage('Please enter the YCloud API key!')
      return false
    }
    return true
  }

  const logSafeError = (error: unknown) => {
    const status = (error as { response?: { status?: number } })?.response?.status
    const traceId = (error as { response?: { headers?: Record<string, string> } })?.response?.headers?.['x-trace-id']
    console.error('ycloud.save failed', { status, traceId })
  }

  const callSubmit = async () => {
    if (!isValidForm()) return
    const payload = {
      enabled: ycloudAnalytics?.enabled,
      api_key: ycloudAnalytics?.apiKey?.trim(),
      channels: getEnabledChannelsArray(ycloudAnalytics?.channels)
    }
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const verifyResponse = (await verifyAnalyticsConfig('ycloud', payload)) as ApiResponse
      if (verifyResponse?.error) {
        setOpenAlert('error')
        setAlertMessage(verifyResponse?.error)
        setLoaderProperties({ isLoaderVisible: false })
        return
      }

      const response = (await setAnalyticsConfig('ycloud', payload)) as ApiResponse
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations({ ...integrations, ycloud: ycloudAnalytics }, false)
        setOpenAlert('success')
        setAlertMessage('Successfully saved analytics configuration!')
        handleOnboardingSave && handleOnboardingSave()
      }
    } catch (error) {
      const errorMessage =
        (error as { response?: { data?: { errorMessage?: string } } })?.response?.data?.errorMessage ??
        'Could not save configuration!'
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      logSafeError(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleDiscardSaving = () => {
    if (!ycloud) return
    setYcloudAnalytics(ycloud)
    setTemporaryData(undefined)
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (_event: React.SyntheticEvent | Event, reason: string) => {
    if (reason === 'clickaway') return
    handleAlerts('')
  }

  return (
    <>
      <IntegrationChannels
        channels={ycloudAnalytics.channels ?? []}
        onChange={(channels: ChannelConfigType[]) => setYcloudAnalytics({ ...ycloudAnalytics, channels })}
        hideChannels={!ycloudAnalytics?.enabled}
      >
        <div className='flex flex-col w-full gap-8 py-4'>
          <div className='flex flex-col gap-4'>
            <label htmlFor={INPUT_ID} className='text-text-xs-medium text-zinc-500'>
              API key
            </label>
            <Input
              id={INPUT_ID}
              variant='outline'
              dimension='md'
              type={showApiKey ? 'text' : 'password'}
              placeholder='xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'
              className='max-w-[420px]'
              autoComplete='off'
              spellCheck={false}
              data-lpignore='true'
              data-1p-ignore
              value={ycloudAnalytics?.apiKey ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setYcloudAnalytics({ ...ycloudAnalytics, apiKey: event.target.value })
              }
              endAdornment={
                <button
                  type='button'
                  onClick={() => setShowApiKey(!showApiKey)}
                  className='flex items-center justify-center text-zinc-500 hover:text-zinc-700'
                  aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                >
                  <Icon IconComponent={showApiKey ? EyeSlash : Eye} size='sm' />
                </button>
              }
            />
            <p className='text-text-xs-regular text-zinc-500'>
              Find your API key in the YCloud console under Developers → API keys. Abandoned checkouts trigger the
              checkout_abandon_notification event your YCloud Journeys can act on.
            </p>
          </div>

          <div className='flex items-center justify-between'>
            <label id={TOGGLE_LABEL_ID} className='flex items-center gap-2 text-text-sm-regular text-zinc-700'>
              <span>YCloud analytics is</span>
              <span className='text-text-sm-medium text-zinc-800'>
                {ycloudAnalytics?.enabled ? 'enabled' : 'disabled'}
              </span>
            </label>
            <Switch
              name='ycloudToggle'
              checked={ycloudAnalytics?.enabled ?? false}
              inputProps={{ 'aria-labelledby': TOGGLE_LABEL_ID }}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setYcloudAnalytics({ ...ycloudAnalytics, enabled: event.target.checked })
              }
            />
          </div>
        </div>
      </IntegrationChannels>

      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={callSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <button
          className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
          onClick={callSubmit}
        >
          {t('save')}
        </button>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}

export default Ycloud
